import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

type RealtimeEvent =
  | 'new-notification'
  | 'new-message'
  | 'new-reply'
  | 'task-updated'
  | 'discussion-read';

/**
 * Hook to listen for real-time events and invalidate queries
 */
export function useRealtimeInvalidation(
  event: RealtimeEvent | RealtimeEvent[],
  queryKeys: string[][],
  filter?: (data: any) => boolean
) {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    const events = Array.isArray(event) ? event : [event];
    
    const handleEvent = (e: CustomEvent) => {
      if (filter && !filter(e.detail)) return;
      
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    };
    
    events.forEach((evt) => {
      window.addEventListener(evt, handleEvent as EventListener);
    });
    
    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleEvent as EventListener);
      });
    };
  }, [event, queryKeys, filter, queryClient]);
}

/**
 * Hook to listen for real-time events with a callback
 */
export function useRealtimeEvent<T = any>(
  event: RealtimeEvent,
  callback: (data: T) => void
) {
  useEffect(() => {
    const handleEvent = (e: CustomEvent<T>) => {
      callback(e.detail);
    };
    
    window.addEventListener(event, handleEvent as EventListener);
    
    return () => {
      window.removeEventListener(event, handleEvent as EventListener);
    };
  }, [event, callback]);
}

/**
 * Hook to handle new notification toast
 */
export function useNotificationToast() {
  useRealtimeEvent('new-notification', (notification: any) => {
    // Show a toast notification
    // You can replace this with a proper toast library
    console.log('New notification:', notification.title);
  });
}


